import { useEffect, useRef, type MutableRefObject } from 'react'

type ChatPanelToggleFocusArgs = {
  draftArtifactVersionId: string | null
  chatPanelOpen: boolean
  isSmallScreen: boolean
  /* Set when something other than the toggles opened the panel, so focus stays where the reader left it. */
  openedWithoutToggleRef?: MutableRefObject<boolean>
}

/**
 * Keeps keyboard focus on the chat panel's toggle as it moves between the panel header and the floating
 * button, each of which unmounts as the panel opens or closes.
 *
 * Give the returned refs to those two buttons.
 */
export function useChatPanelToggleFocus({ draftArtifactVersionId, chatPanelOpen, isSmallScreen, openedWithoutToggleRef }: ChatPanelToggleFocusArgs) {
  const collapseButtonRef = useRef<HTMLButtonElement>(null)
  const reopenButtonRef = useRef<HTMLButtonElement>(null)
  const previousStateRef = useRef({ draftArtifactVersionId, chatPanelOpen })

  useEffect(() => {
    const previousState = previousStateRef.current
    previousStateRef.current = { draftArtifactVersionId, chatPanelOpen }
    // Only a toggle within one draft follows from pressing one of the buttons; a draft switch does not.
    if (isSmallScreen || previousState.draftArtifactVersionId !== draftArtifactVersionId) return
    if (previousState.chatPanelOpen === chatPanelOpen) return
    if (openedWithoutToggleRef?.current) {
      openedWithoutToggleRef.current = false
      return
    }
    const focusTarget = chatPanelOpen ? collapseButtonRef.current : reopenButtonRef.current
    focusTarget?.focus()
  }, [chatPanelOpen, draftArtifactVersionId, isSmallScreen, openedWithoutToggleRef])

  return { collapseButtonRef, reopenButtonRef }
}
